import React, { Component, ReactNode, useState } from 'react';
import { STACItem } from './types/stac';
import MapViewer from './components/MapViewer';
import SearchPanel from './components/SearchPanel';
import AnalysisSelector from './components/AnalysisSelector';
import TimeseriesAnalysis from './components/TimeseriesAnalysis';
import ProvinceSelector from './components/ProvinceSelector';
import { RWANDA_CONFIG, getAnalysisPreset } from './config/rwanda';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: any) {
    console.error('Error boundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <h3 className="text-red-800 font-medium">Something went wrong</h3>
          <p className="text-red-600 text-sm mt-1">
            {this.state.error?.message || 'An unexpected error occurred'}
          </p>
        </div>
      );
    }

    return this.props.children;
  }
}

const App: React.FC = () => {
  const [selectedProvince, setSelectedProvince] = useState<string | null>(null);
  const [selectedItems, setSelectedItems] = useState<STACItem[]>([]);
  const [analysisType, setAnalysisType] = useState<string>('agriculture');
  const [timeRange, setTimeRange] = useState<{ start: Date; end: Date }>({
    start: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000), // 1 year ago
    end: new Date()
  });

  return (
    <ErrorBoundary>
      <div className="h-screen flex flex-col bg-gray-50">
        {/* Header */}
        <header className="bg-gradient-to-r from-rwanda-blue via-rwanda-green to-rwanda-yellow text-white shadow-lg">
          <div className="container mx-auto px-4 py-3 flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                <span className="text-rwanda-blue font-bold text-lg">🇷🇼</span>
              </div>
              <div>
                <h1 className="text-xl font-bold">Rwanda Earth Observation Viewer</h1>
                <p className="text-sm text-blue-100">Powered by EO2Cube STAC API</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <ProvinceSelector 
                provinces={RWANDA_CONFIG.PROVINCES}
                selected={selectedProvince}
                onSelect={setSelectedProvince}
              />
              <AnalysisSelector
                analysisTypes={Object.keys(RWANDA_CONFIG.ANALYSIS_PRESETS)}
                selected={analysisType}
                onSelect={setAnalysisType}
              />
              <div className="text-sm">
                <span className="text-green-200">●</span> Connected to STAC
              </div>
            </div>
          </div>
        </header>

        {/* Main content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left sidebar - Search */}
          <div className="w-80 border-r border-gray-200">
            <SearchPanel
              selectedProvince={selectedProvince}
              selectedItems={selectedItems}
              onItemsChange={setSelectedItems}
              timeRange={timeRange}
              onTimeRangeChange={setTimeRange}
            />
          </div>

          {/* Center - Map */}
          <div className="flex-1 flex flex-col">
            <MapViewer
              selectedProvince={selectedProvince}
              selectedItems={selectedItems}
            />
          </div>

          {/* Right sidebar - Analysis */}
          <div className="w-96 border-l border-gray-200">
            <TimeseriesAnalysis
              selectedItems={selectedItems}
              analysisType={analysisType}
              timeRange={timeRange}
              province={selectedProvince}
            />
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-gray-800 text-white py-2 px-4 text-sm">
          <div className="flex justify-between items-center">
            <div>
              Rwanda STAC Inspector v1.0.0 | {selectedItems.length} items selected
            </div>
            <div>
              {selectedProvince ? `${selectedProvince} Province` : 'All Rwanda'} | 
              Analysis: {getAnalysisPreset(analysisType)?.name || analysisType}
            </div>
          </div>
        </footer>
      </div>
    </ErrorBoundary>
  );
};

export default App;